import type {
  AppealStatus,
  FraudStatus,
  Market,
  SuspensionReasonCode,
  User,
} from '../dataProvider/types';

/**
 * The Suspension-flow query/aggregation module (D-suspension-flow) - the ONE place
 * the suspended-users worklist is assembled and the elapsed state is derived. Same
 * seam idiom as the fraud queue / subscriptions / referrals: v1 aggregates
 * client-side from the mock provider, every selector lives here, and the late
 * real-data migration swaps this module for a server-side endpoint without the
 * surface changing.
 *
 * It builds on the fraud foundation (#45): a suspension is `fraudStatus` ===
 * 'suspended' carrying `suspensionEndsAt`. This surface makes the LIFECYCLE
 * first-class: it derives off the injected clock that a suspension whose period has
 * passed is "elapsed, pending lift" (the promo-lifecycle pattern, NO stored write -
 * there is no engine and the human still performs the reinstate), and it organises
 * the work as a WORKLIST: elapsed-pending-lift on top, pending appeals as their own
 * section, running suspensions below.
 *
 * INVESTIGATIVE-GLOBAL, inherited from the fraud queue: it does NOT read the region
 * switcher (a fraud ring spans markets). The optional `regionFilter` (the surface's
 * own facet) narrows by the user's market by exact value.
 */

const DAY = 86_400_000;

/** The ISO instant a suspension applied at `now` for `days` should end. The
 *  admin-chosen-period replacement for the old fixed `suspensionEnd`. */
export const suspensionEndAfter = (now: number, days: number): string =>
  new Date(now + days * DAY).toISOString();

/** A running hold still inside its period, vs one whose period has elapsed (due
 *  for a lift). Derived off the injected clock, never stored. */
export type SuspensionPhase = 'running' | 'elapsed';

export interface SuspensionRow {
  id: number;
  name: string;
  email: string;
  market: Market;
  fraudStatus: FraudStatus;
  suspensionEndsAt: string | null;
  suspensionReasonCode: SuspensionReasonCode | null;
  fraudReason: string | null;
  appealStatus: AppealStatus;
  appealedAt: string | null;
  appealNote: string | null;
  /** Derived off the clock; null for a banned account (no period). */
  phase: SuspensionPhase | null;
}

export interface SuspensionsView {
  /** Pending appeals (suspended OR banned), oldest appeal first. Worked here. */
  pendingAppeals: SuspensionRow[];
  /** Suspended, period elapsed, no pending appeal: due for a lift, on top. */
  elapsed: SuspensionRow[];
  /** Suspended, period still running, no pending appeal. */
  running: SuspensionRow[];
}

export interface SuspensionsInput {
  users: User[];
}

export interface SuspensionsOptions {
  /** The injected clock the elapsed state is measured against. */
  now: number;
  /** Exact-value market facet (uk / us), or null for the global view. */
  regionFilter?: Market | null;
  /** Structured reason-code facet, or null for every reason. */
  reasonFilter?: SuspensionReasonCode | null;
}

const phaseFor = (u: User, now: number): SuspensionPhase | null => {
  if (u.fraudStatus !== 'suspended' || u.suspensionEndsAt == null) return null;
  return now > Date.parse(u.suspensionEndsAt) ? 'elapsed' : 'running';
};

const toRow = (u: User, now: number): SuspensionRow => ({
  id: u.id,
  name: u.name,
  email: u.email,
  market: u.market,
  fraudStatus: u.fraudStatus,
  suspensionEndsAt: u.suspensionEndsAt,
  suspensionReasonCode: u.suspensionReasonCode,
  fraudReason: u.fraudReason,
  appealStatus: u.appealStatus,
  appealedAt: u.appealedAt,
  appealNote: u.appealNote,
  phase: phaseFor(u, now),
});

const appealedFirst = (a: SuspensionRow, b: SuspensionRow): number =>
  (a.appealedAt ?? '').localeCompare(b.appealedAt ?? '') ||
  a.name.localeCompare(b.name);

const endsSoonestFirst = (a: SuspensionRow, b: SuspensionRow): number =>
  (a.suspensionEndsAt ?? '').localeCompare(b.suspensionEndsAt ?? '') ||
  a.name.localeCompare(b.name);

export function buildSuspensionsView(
  input: SuspensionsInput,
  opts: SuspensionsOptions,
): SuspensionsView {
  const { now } = opts;
  // The base set: every suspended account, PLUS any account with a pending appeal
  // (so a banned account that is contesting surfaces on the appeal worklist). An
  // active account never appears.
  const inScope = input.users
    .filter(
      (u) => u.fraudStatus === 'suspended' || u.appealStatus === 'pending',
    )
    .filter((u) => opts.regionFilter == null || u.market === opts.regionFilter)
    .filter(
      (u) =>
        opts.reasonFilter == null ||
        u.suspensionReasonCode === opts.reasonFilter,
    )
    .map((u) => toRow(u, now));

  const pendingAppeals = inScope
    .filter((r) => r.appealStatus === 'pending')
    .sort(appealedFirst);
  const heldOnly = inScope.filter(
    (r) => r.fraudStatus === 'suspended' && r.appealStatus !== 'pending',
  );
  const elapsed = heldOnly
    .filter((r) => r.phase === 'elapsed')
    .sort(endsSoonestFirst);
  const running = heldOnly
    .filter((r) => r.phase === 'running')
    .sort(endsSoonestFirst);

  return { pendingAppeals, elapsed, running };
}
